import { getQuestionCountFromDB } from './questionService';

export interface QuestionCounts {
  sat: { total: number; math: number; english: number };
  act: { total: number; english: number; math: number; reading: number; science: number };
  apPhysics: { total: number };
  apCalc: { total: number };
  grand: number;
}

// Round down to nearest 100 for large counts, keep exact for small ones.
export function formatCount(n: number): string {
  if (n <= 0) return '0';
  const rounded = n >= 1000 ? Math.floor(n / 100) * 100 : n;
  return rounded.toLocaleString() + '+';
}

const CACHE_KEY = 'scholar_question_counts_v1';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export async function fetchAllQuestionCounts(): Promise<QuestionCounts> {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const { data, ts } = JSON.parse(raw) as { data: QuestionCounts; ts: number };
      if (Date.now() - ts < CACHE_TTL_MS) return data;
    }
  } catch { /* ignore parse errors */ }

  const [satMath, satEnglish, actEnglish, actMath, actReading, actScience, apPhysics, apCalc] =
    await Promise.all([
      getQuestionCountFromDB('sat', 'math'),
      getQuestionCountFromDB('sat', 'english'),
      getQuestionCountFromDB('act', 'english'),
      getQuestionCountFromDB('act', 'math'),
      getQuestionCountFromDB('act', 'reading'),
      getQuestionCountFromDB('act', 'science'),
      getQuestionCountFromDB('ap-physics-1'),
      getQuestionCountFromDB('ap-calc-bc'),
    ]);

  const counts: QuestionCounts = {
    sat: { total: satMath + satEnglish, math: satMath, english: satEnglish },
    act: {
      total: actEnglish + actMath + actReading + actScience,
      english: actEnglish,
      math: actMath,
      reading: actReading,
      science: actScience,
    },
    apPhysics: { total: apPhysics },
    apCalc: { total: apCalc },
    grand: satMath + satEnglish + actEnglish + actMath + actReading + actScience + apPhysics + apCalc,
  };

  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: counts, ts: Date.now() }));
  } catch { /* ignore storage errors */ }

  return counts;
}
